import React, { useEffect, useState } from 'react';
import { Download, Loader2, RefreshCw, Sparkles, Tag, User, X } from 'lucide-react';
import Modal from '../../components/Modal';
import StatusBadge from '../../components/StatusBadge';
import { ModalFormInput } from '../../components/FormComponents';
import { useToast } from '../../components/ToastProvider';
import { CONTRACT_STATUSES } from '../../constants/statuses';
import BatchRunProgress, { previewLine } from '../structured-data/BatchRunProgress';
import useBatchRun from '../structured-data/useBatchRun';
import { getSchemas, previewBatchRun } from '../structured-data/structuredDataApi';
import { bulkDownloadFiles, bulkUpdateOwner, bulkUpdateStatus, bulkUpdateTags } from './contractBulkApi';

const ACTION_TITLES = {
  status: 'Set status',
  owner: 'Set internal owner',
  tags: 'Edit tags',
  schema: 'Run schema',
  download: 'Download file',
};

// Sticky bar shown while list rows are selected: count + the bulk operations
// (status / owner / tag / run schema / download), each confirmed through a
// small modal. Selection is page-scoped; the caller clears it and refreshes
// after apply. Contract changes require write_contracts; extraction requires
// run_schemas. Download renders for everyone who can see the contracts.
export default function BulkActionsBar({
  canWriteContracts,
  canRunSchemas = canWriteContracts,
  selectedIds,
  tagOptions,
  contracts = [],
  onClearSelection,
  onApplied,
}) {
  const toast = useToast();
  const [action, setAction] = useState(null); // null | 'status' | 'owner' | 'tags' | 'schema' | 'download'
  const [busy, setBusy] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [owner, setOwner] = useState('');
  const [tagMode, setTagMode] = useState('add');
  const [tagValue, setTagValue] = useState('');

  // Run-schema state: available schemas, the pick, and the dry-count preview.
  const [schemas, setSchemas] = useState(null);         // null until loaded
  const [schemasError, setSchemasError] = useState(null);
  const [schemaId, setSchemaId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const batchRun = useBatchRun({
    onSettled: (batch) => {
      const c = batch.counts;
      const summary = [`${c.done} run`];
      if (c.skippedFresh > 0) summary.push(`${c.skippedFresh} already up to date`);
      if (c.failed > 0) summary.push(`${c.failed} failed`);
      if (c.failed > 0) toast.warning('Run schema — partial', `${summary.join(', ')}.`);
      else toast.success('Run schema', `${summary.join(', ')}.`);
      onApplied();
    },
  });

  const count = selectedIds.size;

  // Load the schema list once the Run-schema modal first opens.
  useEffect(() => {
    if (action !== 'schema' || schemas !== null) return;
    let cancelled = false;
    getSchemas()
      .then((res) => {
        if (cancelled) return;
        setSchemas((res.schemas || []).filter((s) => s.active));
      })
      .catch((err) => {
        if (cancelled) return;
        setSchemasError(err.message);
      });
    return () => { cancelled = true; };
  }, [action, schemas]);

  // Preview the picked schema against the selection for the count line.
  useEffect(() => {
    if (action !== 'schema' || !schemaId) { setPreview(null); return undefined; }
    let cancelled = false;
    setPreview(null);
    setPreviewError(null);
    previewBatchRun(schemaId, { contractIds: [...selectedIds] })
      .then((res) => { if (!cancelled) setPreview(res); })
      .catch((err) => { if (!cancelled) setPreviewError(err.message); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, schemaId]);

  if (count === 0) return null;

  const openAction = (next) => {
    setNewStatus('');
    setOwner('');
    setTagMode('add');
    setTagValue('');
    setSchemaId('');
    setPreview(null);
    setPreviewError(null);
    batchRun.reset();
    setAction(next);
  };

  const closeModal = () => {
    if (busy || batchRun.starting || batchRun.inFlight) return;
    setAction(null);
  };

  const applyDisabled = busy ||
    (action === 'status' && !newStatus) ||
    (action === 'tags' && !tagValue.trim()) ||
    (action === 'schema' && (!schemaId || !preview || preview.willRun === 0 || batchRun.starting));

  const apply = async () => {
    const ids = [...selectedIds];
    if (action === 'schema') {
      // Fire the batch; the modal flips to progress and settles via the hook.
      await batchRun.start(schemaId, { contractIds: ids });
      return;
    }
    if (action === 'download') {
      setBusy(true);
      try {
        const byId = new Map(contracts.map((c) => [c.id, c.title || c.shortName || c.id]));
        const res = await bulkDownloadFiles(ids, {
          fallbackFilename: ids.length === 1 ? (byId.get(ids[0]) || 'contract-file') : 'docket-contracts.zip',
        });
        const skippedNames = res.skippedIds.map((id) => byId.get(id) || id);
        if (res.downloaded === 0) {
          toast.warning(
            'Download file',
            `Nothing downloaded — ${count === 1 ? 'the selected contract has no file' : 'none of the selected contracts have a file'}.`
          );
        } else if (res.skippedIds.length > 0) {
          toast.warning(
            'Download file — partial',
            `Downloaded ${res.downloaded} of ${ids.length} — ${res.skippedIds.length} ${res.skippedIds.length === 1 ? 'contract has' : 'contracts have'} no file: ${skippedNames.join(', ')}.`
          );
        } else {
          toast.success('Download file', `Downloaded ${res.downloaded} ${res.downloaded === 1 ? 'file' : 'files'}.`);
        }
        // No onApplied(): downloading changes nothing server-side, so the
        // list doesn't need a refresh and the selection stays useful.
        setAction(null);
      } catch (err) {
        toast.error('Download failed', err.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      let result;
      if (action === 'status') {
        result = await bulkUpdateStatus(ids, newStatus);
      } else if (action === 'owner') {
        result = await bulkUpdateOwner(ids, owner.trim());
      } else {
        const tag = tagValue.trim();
        result = await bulkUpdateTags(ids, tagMode === 'add' ? { add: [tag] } : { remove: [tag] });
      }

      const unchanged = (result.results || []).filter((r) => r.ok && !r.changed).length;
      const parts = [`${result.updated} updated`];
      if (unchanged > 0) parts.push(`${unchanged} already up to date`);
      if (result.failed > 0) parts.push(`${result.failed} not found`);
      const summary = `${parts.join(', ')}.`;
      if (result.failed > 0) {
        toast.warning(`${ACTION_TITLES[action]} — partial`, summary);
      } else {
        toast.success(ACTION_TITLES[action], summary);
      }
      setAction(null);
      onApplied();
    } catch (err) {
      toast.error('Bulk update failed', err.message);
    } finally {
      setBusy(false);
    }
  };

  const existingTags = tagOptions.filter((o) => o.value);
  const schemaFooter = batchRun.batch !== null || batchRun.starting
    ? (
      <button
        type="button"
        onClick={() => { setAction(null); batchRun.reset(); }}
        className="btn outline sm"
        disabled={batchRun.starting}
      >
        {batchRun.settled ? 'Close' : 'Run in background'}
      </button>
    )
    : null;

  return (
    <>
      <div className="bulk-bar animate-fade-in" role="toolbar" aria-label="Bulk actions">
        <span className="bulk-bar-count">
          {count} {count === 1 ? 'contract' : 'contracts'} selected
        </span>
        <div className="bulk-bar-actions">
          {canWriteContracts && (
            <>
              <button type="button" className="btn outline sm" onClick={() => openAction('status')}>
                <RefreshCw className="w-3.5 h-3.5" />
                Set status
              </button>
              <button type="button" className="btn outline sm" onClick={() => openAction('owner')}>
                <User className="w-3.5 h-3.5" />
                Set owner
              </button>
              <button type="button" className="btn outline sm" onClick={() => openAction('tags')}>
                <Tag className="w-3.5 h-3.5" />
                Tags
              </button>
            </>
          )}
          {canRunSchemas && (
              <button type="button" className="btn outline sm" onClick={() => openAction('schema')}>
                <Sparkles className="w-3.5 h-3.5" />
                Run schema
              </button>
          )}
          <button type="button" className="btn outline sm" onClick={() => openAction('download')}>
            <Download className="w-3.5 h-3.5" />
            Download file
          </button>
        </div>
        <button type="button" className="btn ghost sm" onClick={onClearSelection}>
          <X className="w-3.5 h-3.5" />
          Clear
        </button>
      </div>

      <Modal
        isOpen={action !== null}
        onClose={closeModal}
        title={action ? ACTION_TITLES[action] : ''}
        description={`Applies to the ${count} selected ${count === 1 ? 'contract' : 'contracts'}.`}
        size="sm"
        footer={action === 'schema' && schemaFooter ? schemaFooter : (
          <>
            <button type="button" onClick={closeModal} className="btn outline sm" disabled={busy}>
              Cancel
            </button>
            <button type="button" onClick={apply} className="btn primary sm" disabled={applyDisabled}>
              {(busy || batchRun.starting) && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {action === 'schema' ? 'Run' : action === 'download' ? 'Download' : 'Apply'}
            </button>
          </>
        )}
      >
        <div className="card-body">
          {action === 'status' && (
            <>
              <div className="field-label" style={{ marginBottom: 8 }}>Select new status</div>
              {/* Same rule as the single-contract Change Status modal:
                  'terminated' only via the dedicated Terminate flow, which
                  captures a reason onto the status_history note. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {CONTRACT_STATUSES.filter((s) => s.value !== 'terminated').map((s) => {
                  const selected = newStatus === s.value;
                  return (
                    <label
                      key={s.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 10,
                        borderRadius: 6,
                        border: selected ? '0.5px solid var(--border-focus)' : '0.5px solid var(--border-primary)',
                        background: selected ? 'var(--surface-hover)' : 'transparent',
                        cursor: 'pointer',
                        transition: 'background 120ms, border-color 120ms',
                      }}
                    >
                      <input
                        type="radio"
                        name="bulk-status"
                        value={s.value}
                        checked={selected}
                        onChange={(e) => setNewStatus(e.target.value)}
                        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                      />
                      <span style={{
                        width: 14, height: 14, borderRadius: '50%',
                        border: selected ? '1px solid var(--border-focus)' : '1px solid var(--border-secondary)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        {selected && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-primary)' }} />}
                      </span>
                      <StatusBadge status={s.value} size="small" />
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {action === 'owner' && (
            <>
              <ModalFormInput
                label="Internal owner"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                placeholder="Person responsible…"
                autoFocus
              />
              <div className="field-hint" style={{ marginTop: 6 }}>
                Leave empty to clear the owner on the selected contracts.
              </div>
            </>
          )}

          {action === 'tags' && (
            <>
              <div className="field-label" style={{ marginBottom: 8 }}>Operation</div>
              <div className="row" style={{ gap: 8, marginBottom: 14 }}>
                <button
                  type="button"
                  className={tagMode === 'add' ? 'btn sm' : 'btn outline sm'}
                  onClick={() => setTagMode('add')}
                >
                  Add tag
                </button>
                <button
                  type="button"
                  className={tagMode === 'remove' ? 'btn sm' : 'btn outline sm'}
                  onClick={() => setTagMode('remove')}
                >
                  Remove tag
                </button>
              </div>
              <ModalFormInput
                label={tagMode === 'add' ? 'Tag to add' : 'Tag to remove'}
                value={tagValue}
                onChange={(e) => setTagValue(e.target.value)}
                placeholder="e.g. Initiative-X"
                list="bulk-tag-options"
                autoFocus
              />
              <datalist id="bulk-tag-options">
                {existingTags.map((o) => <option key={o.value} value={o.value} />)}
              </datalist>
            </>
          )}

          {action === 'download' && (
            <div className="stack-sm" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              <div>
                Downloads each contract&apos;s copy of record — the executed version when one is
                marked, otherwise the latest uploaded document.
              </div>
              <div>
                {count === 1
                  ? 'One contract selected: the file downloads directly.'
                  : 'The files download together as a single zip.'}
                {' '}Contracts without a document are skipped and reported.
              </div>
            </div>
          )}

          {action === 'schema' && (
            (batchRun.batch !== null || batchRun.starting) ? (
              batchRun.batch === null ? (
                <div className="row" style={{ gap: 8, alignItems: 'center', fontSize: 13 }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ flexShrink: 0 }} />
                  Starting the batch…
                </div>
              ) : (
                <BatchRunProgress batch={batchRun.batch} />
              )
            ) : (
              <div className="stack-sm">
                {schemasError && (
                  <div className="field-hint" style={{ color: 'var(--text-critical)' }}>
                    Couldn't load schemas: {schemasError}
                  </div>
                )}
                {!schemasError && schemas === null && (
                  <div className="muted" style={{ fontSize: 12.5 }}>Loading schemas…</div>
                )}
                {schemas !== null && schemas.length === 0 && (
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    No active data schemas yet — an admin can create one under Settings → Data schemas.
                  </div>
                )}
                {schemas !== null && schemas.length > 0 && (
                  <>
                    <div className="field-label" style={{ marginBottom: 6 }}>Schema</div>
                    <select
                      className="select"
                      value={schemaId}
                      onChange={(e) => setSchemaId(e.target.value)}
                      aria-label="Schema to run"
                    >
                      <option value="">Choose a schema…</option>
                      {schemas.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <div className="field-hint" style={{ marginTop: 8, minHeight: 18 }} aria-live="polite">
                      {previewError && <span style={{ color: 'var(--text-critical)' }}>Preview failed: {previewError}</span>}
                      {!previewError && schemaId && !preview && 'Checking the selection…'}
                      {preview && previewLine(preview)}
                      {preview && preview.willRun === 0 && ' Nothing to run.'}
                    </div>
                    {batchRun.error && (
                      <div className="field-hint" style={{ color: 'var(--text-critical)' }}>
                        Couldn't start the run: {batchRun.error}
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          )}
        </div>
      </Modal>
    </>
  );
}
